import { unwrapResult } from '@reduxjs/toolkit';
import { Button, Input, Modal, Select, message } from 'antd';
import Loading from 'components/Loading';
import { INIT_PAGINATION } from 'constants/common.constant';
import { capitalizeFirstLetter, getUniqueKey } from 'helpers/common.helper';
import { IQueryParam } from 'interfaces/common.interface';
import { debounce, remove } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Card, CardBody, Col, Row, Table } from 'reactstrap';
import { createVipCode, getVipCode, removeVipCode } from 'store/global/action';
import { useAppDispatch } from 'store/hooks';

const { Option } = Select;

const VIP_CODE_STATUS = [
  {
    name: 'Active',
    value: 'active',
  },
  {
    name: 'Redeemed',
    value: 'redeemed',
  },
  {
    name: 'Revoked',
    value: 'revoked',
  },
];

const VipCode = () => {
  const dispatch = useAppDispatch();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState<IQueryParam>({
    page: INIT_PAGINATION.PAGE,
    limit: INIT_PAGINATION.LIMIT,
  });
  const [pagination, setPagination] = useState<any>(null);
  const [hasMore, setHasMore] = useState(true);
  const [showDeleteVipCode, setShowDeleteVipCode] = useState(false);
  const [deleteId, setDeleteId] = useState<any>(null);
  const [createCodeLoading, setCreateCodeLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);

      const res: any = await unwrapResult(await dispatch(getVipCode(params)));

      if (res && res.items && res.pagination) {
        setData([...data, ...res.items]);
        setPagination(res.pagination);
      } else {
        setData([...[]]);
        setPagination(null);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreData = () => {
    if (params.page && params.page < pagination.totalPages) {
      setParams({ ...params, page: params.page + 1 });
    }
  };

  const handleRemoveVipCode = (id: string) => {
    setDeleteId(id);
    setShowDeleteVipCode(true);
  };

  const resetRemoveModal = () => {
    setDeleteId(null);
    setShowDeleteVipCode(false);
  };

  const handleCancelRemoveVipCode = () => {
    resetRemoveModal();
  };

  const handleOkRemoveVipCode = async () => {
    try {
      await unwrapResult(await dispatch(removeVipCode(deleteId)));
      remove(data, (item) => item._id === deleteId);
      setData([...data]);
      message.success('Deleted VIP code successfully');
      resetRemoveModal();
    } catch (e) {
      console.log(e);
    }
  };

  const resetData = () => {
    setData([...[]]);
    setPagination(null);
  };

  const searching = (e: any) => {
    resetData();

    if (e.target.value.trim() !== '') {
      setParams({
        ...params,
        keyword: e.target.value,
        page: INIT_PAGINATION.PAGE,
        limit: INIT_PAGINATION.LIMIT,
      });
    } else {
      setParams({
        ...params,
        keyword: null,
        page: INIT_PAGINATION.PAGE,
        limit: INIT_PAGINATION.LIMIT,
      });
    }
  };

  const selectStatus = (value: any) => {
    resetData();

    if (value !== '') {
      setParams({
        ...params,
        status: value,
        page: INIT_PAGINATION.PAGE,
        limit: INIT_PAGINATION.LIMIT,
      });
    } else {
      setParams({
        ...params,
        status: null,
        page: INIT_PAGINATION.PAGE,
        limit: INIT_PAGINATION.LIMIT,
      });
    }
  };

  const generateCode = async () => {
    try {
      setCreateCodeLoading(true);
      await unwrapResult(await dispatch(createVipCode()));
      message.success('Generated VIP Code successfully');
      resetData();
      setParams({
        ...params,
        page: INIT_PAGINATION.PAGE,
        limit: INIT_PAGINATION.LIMIT,
      });
    } catch (e) {
      console.log(e);
      message.error('Something went wrong');
    } finally {
      setCreateCodeLoading(false);
    }
  };

  useEffect(() => {
    if (params) {
      fetchData();
    }
  }, [params]);

  useEffect(() => {
    if (pagination) {
      if (pagination.currentPage < pagination.totalPages) {
        setHasMore(true);
      } else {
        setHasMore(false);
      }
    }
  }, [pagination]);

  return (
    <>
      <div className="content user-content">
        <Row>
          <div className="multiple-fields">
            <div className="multiple-fields__item">
              <Input
                className="default"
                placeholder="Search..."
                allowClear
                onChange={debounce(searching, 1000)}
              />
            </div>

            <div className="multiple-fields__item">
              <Select
                placeholder="Select status"
                className="default"
                allowClear
                onChange={selectStatus}
              >
                {VIP_CODE_STATUS.map((item) => (
                  <Option key={getUniqueKey()} value={item.value}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="multiple-fields__item">
              <Button
                className="btn btn-primary"
                onClick={generateCode}
                loading={createCodeLoading}
              >
                Generate VIP Code
              </Button>
            </div>
          </div>
        </Row>

        <Row>
          <Col md="12">
            {data?.length > 0 ? (
              <Card>
                <CardBody>
                  <InfiniteScroll
                    dataLength={data.length}
                    next={fetchMoreData}
                    hasMore={hasMore}
                    loader={null}
                    endMessage={
                      <p className="all-items-txt">You have seen all items</p>
                    }
                  >
                    <Table responsive>
                      <thead className="text-primary">
                        <tr>
                          <th>Code</th>
                          <th>Status</th>
                          <th>Redeemed By</th>
                          <th>Redeemed At</th>
                          <th>Created At</th>
                          <th className="text-right">Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {data.map((item) => (
                          <tr key={getUniqueKey()}>
                            <td>{item.code}</td>
                            <td>{capitalizeFirstLetter(item.status)}</td>
                            <td>
                              {item.redeemedBy ? (
                                <a href={`user/${item.redeemedBy._id}`}>
                                  {item.redeemedBy.fullName}
                                </a>
                              ) : (
                                '_'
                              )}
                            </td>
                            <td>
                              {item.redeemedAt
                                ? moment(item.redeemedAt).format(
                                    'YYYY-MM-DD HH:mm:ss',
                                  )
                                : '_'}
                            </td>
                            <td>
                              {moment(item.createdAt).format(
                                'YYYY-MM-DD HH:mm:ss',
                              )}
                            </td>
                            <td className="text-right action link">
                              <i
                                className="fas fa-trash"
                                onClick={() => handleRemoveVipCode(item._id)}
                              ></i>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </InfiniteScroll>
                </CardBody>
              </Card>
            ) : (
              <p className="all-items-txt">No items.</p>
            )}

            {loading && <Loading />}
          </Col>
        </Row>
      </div>

      <Modal
        title="Delete"
        open={showDeleteVipCode}
        onOk={handleOkRemoveVipCode}
        onCancel={handleCancelRemoveVipCode}
        footer={[
          <Button
            key="back"
            className="btn modal-btn"
            onClick={handleCancelRemoveVipCode}
          >
            Cancel
          </Button>,
          <Button
            className="btn btn-primary modal-btn"
            key="submit"
            onClick={handleOkRemoveVipCode}
          >
            OK
          </Button>,
        ]}
      >
        <p>Do you want to remove this code?</p>
      </Modal>
    </>
  );
};

export default VipCode;
